import React from 'react';
import { Leaf, TrendingDown, Zap, Shield, Users, Building2 } from 'lucide-react';

const BenefitCard = ({ icon: Icon, title, description, color }) => {
  return (
    <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group">
      <div className={`w-14 h-14 ${color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-7 h-7 text-emerald-700" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const Benefits = () => {
  const benefits = [
    {
      icon: Leaf,
      title: 'Environmental Benefits',
      description: 'Solar energy produces zero emissions, helping combat climate change and reducing India\'s carbon footprint significantly.',
      color: 'bg-emerald-50'
    },
    {
      icon: TrendingDown,
      title: 'Cost Reduction',
      description: 'Solar power offers competitive tariffs with minimal operating costs, reducing long-term energy expenses for utilities.',
      color: 'bg-amber-50'
    },
    {
      icon: Zap,
      title: 'Energy Security',
      description: 'Domestic solar power reduces dependence on imported fossil fuels, enhancing national energy independence.',
      color: 'bg-blue-50'
    },
    {
      icon: Shield,
      title: 'Reliable Supply',
      description: 'Modern solar plants with storage systems provide stable, predictable power generation for decades.',
      color: 'bg-purple-50'
    },
    {
      icon: Users,
      title: 'Employment Generation',
      description: 'Solar projects create thousands of jobs in manufacturing, installation, and maintenance sectors.',
      color: 'bg-pink-50'
    },
    {
      icon: Building2,
      title: 'Infrastructure Development',
      description: 'Solar installations bring modern infrastructure and economic activity to rural and semi-urban areas.',
      color: 'bg-cyan-50'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Solar Energy</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Solar power is transforming India's energy landscape with multiple benefits for the environment, economy, and society
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} {...benefit} />
          ))}
        </div>

        {/* Statistics Banner */}
        <div className="bg-gradient-to-r from-emerald-700 to-emerald-600 rounded-2xl p-12 text-white">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold mb-2">India's Solar Revolution</h3>
            <p className="text-emerald-100">Leading the global transition to renewable energy</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">500+ GW</div>
              <div className="text-emerald-100">Renewable Energy Target by 2030</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">70+ GW</div>
              <div className="text-emerald-100">Current Solar Capacity</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">₹2.5/kWh</div>
              <div className="text-emerald-100">Competitive Solar Tariffs</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;
